import { useEffect, ReactNode, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as ToasterSonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";

// Import Pages
import ProductRegistration from "./pages/ProductRegistration";
import ProductCatalog from "./pages/ProductCatalog";
import ClientProfileRegistration from "./pages/ClientProfileRegistration";
import AdminOrders from "./pages/AdminOrders";
import OrderHistory from "./pages/OrderHistory";
import NotFound from "./pages/NotFound";
import ClientsProfile from "@/pages/Backoffice/ClientsProfile/components/ClientsProfile";
import OrderManagement from "./pages/Backoffice/OrderManagement";
import OrderDetails from "./pages/CustomerPortal/OrderDetails";
import Dashboard from "./pages/CustomerPortal/Dashboard";
import Index from "./pages/Index";
import { InvoicingList } from "./pages/InvoicingList";

// Import Components
import { SessionExpiredModal } from "@/components/ui/SessionExpiredModal";
import { UserProvider } from "./contexts/UserContext";
import CustomerPortalLayout from "@/components/Layouts/CustomerPortalLayout";
import { AdminRouteGuard } from "./components/guards/AdminRouteGuard";
import { EnvironmentFlag } from "./components/shared/EnvironmentFlag";

import Financial from "./pages/CustomerPortal/Financial/Financial";
import ClientsList from "./pages/Backoffice/Clients/ClientsList";
import ClientDetails from "./pages/Backoffice/Clients/ClientDetails";
import ManageProducts from "./pages/Backoffice/ClientsProfile/ManageProducts";

import { Skeleton } from "./components/ui/skeleton";
import { Loader2 } from "lucide-react";
import { HolidaySuspension } from "./pages/HolidaySuspension";
import MyOrders from "./pages/CustomerPortal/MyOrders";
import OrderInterface from "./components/OrderInterface"; // Desativado: rota realizar-pedido em manutenção
import ScheduledOrder from "./pages/Backoffice/ScheduledOrder";
import SimulatedOrders from "./pages/SimulatedOrders";
import SimulatedOrdersDashboard from "./pages/Backoffice/SimulatedOrdersDashboard";
import MyScheduledOrder from "./pages/CustomerPortal/MyScheduledOrder";
import ProductPricing from "./pages/CustomerPortal/ProductPricing";
import ClientAdjustment from "./pages/ClientAdjustment";
import Simulation2027 from "./pages/CustomerPortal/Simulation2027";
import CustomerPortalOverviewTest from "./pages/CustomerPortal/CustomerPortalOverviewTest";
import CyclesList from "./pages/Backoffice/Cycles/CyclesList";
import OrderMaintenance from "./pages/CustomerPortal/OrderMaintenance";
import SessionSettings from "./pages/SessionSettings";
import { canOverrideDynamicsEnvironment } from "./lib/dynamicsEnvironment";
import { isLocalAuthBypassEnabled } from "./lib/env";
import ScheduledOrderCutoffPolicies from "./pages/Backoffice/ScheduledOrderCutoffPolicies";
import PaymentConfigurations from "./pages/PaymentConfigurations";
import ApiDiagnostics from "./pages/ApiDiagnostics";
import ZendeskTicketSettings from "./pages/ZendeskTicketSettings";
import ScheduledOrderRules from "./pages/Backoffice/ScheduledOrderRules";
import ExamShipmentDistribution from "./pages/Backoffice/ExamShipmentDistribution";
import IntegratedOrders from "./pages/Backoffice/IntegratedOrders/IntegratedOrders";
import { isIntegratedOrdersEnabled } from "./features/integratedOrdersFeature";
import InvoiceReturns from "./pages/Backoffice/InvoiceReturns/InvoiceReturns";
import { isInvoiceReturnsEnabled } from "./features/invoiceReturnsFeature";
import { isExamShipmentDistributionEnabled } from "./features/examShipmentDistributionFeature";
import CouponManagement from "./pages/Backoffice/Coupons/CouponManagement";
import CommercialRevenueDashboard from "./pages/Backoffice/CommercialRevenue/CommercialRevenueDashboard";
import CommercialRevenueClientDetails from "./pages/Backoffice/CommercialRevenue/CommercialRevenueClientDetails";
import { isNewScheduledOrderCycleEnabled } from "./features/newScheduledOrderCycleFeature";
import { isCouponEnabled } from "./features/couponFeature";
import { isNewOrderZendeskTicketEnabled } from "./features/newOrderZendeskTicketFeature";
import OrderItemsReport from "./pages/Backoffice/Reports/OrderItemsReport";
import { isOrderItemsReportEnabled } from "./features/orderItemsReportFeature";
import CustomerGroupImport from "./pages/Backoffice/CustomerGroups/CustomerGroupImport";
import CustomerGroupsList from "./pages/Backoffice/CustomerGroups/CustomerGroupsList";
import { isCustomerGroupAddressEnabled } from "./features/customerGroupAddressFeature";
import { isPhysicalClientEnabled } from "./features/physicalClientFeature";
import PhysicalClientsList from "./pages/Backoffice/PhysicalClients/PhysicalClientsList";
import PhysicalClientCreate from "./pages/Backoffice/PhysicalClients/PhysicalClientCreate";
import PhysicalClientDetails from "./pages/Backoffice/PhysicalClients/PhysicalClientDetails";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos - dados permanecem "frescos"
      refetchOnWindowFocus: false, // Não refaz query ao focar na janela
      refetchOnMount: false, // Não refaz query ao montar se dados ainda estão frescos
      retry: 1, // Tenta novamente apenas 1 vez em caso de erro
    },
  },
});

// Componente de proteção reescrito para usar o 'state' do OIDC.
const ProtectedPage = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLocalAuthBypassEnabled && !auth.isLoading && !auth.isAuthenticated) {
      // Passa a rota desejada pelo 'state' para ser recuperada no callback.
      auth.signinRedirect({ state: { intendedPath: location.pathname + location.search } });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.signinRedirect, location]);

  if (isLocalAuthBypassEnabled) {
    return <>{children}</>;
  }

  if (auth.isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-16 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  // Renderiza um loader enquanto o redirecionamento acontece.
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
};

// Componente simples para a rota de callback para evitar o flash 404.
const AuthCallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-14 h-14 animate-spin text-primary" />
  </div>
);


const App = () => {
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleSessionExpired = () => {
      setIsSessionModalOpen(true);
    };
    window.addEventListener("sessionExpired", handleSessionExpired);
    return () => {
      window.removeEventListener("sessionExpired", handleSessionExpired);
    };
  }, []);

  const handleLoginRedirect = () => {
    setIsSessionModalOpen(false);
    // Passa a rota atual pelo 'state' ao redirecionar.
    auth.signinRedirect({ state: { intendedPath: location.pathname + location.search } });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <EnvironmentFlag />
        <Toaster />
        <ToasterSonner position="bottom-right" />

        <SessionExpiredModal isOpen={isSessionModalOpen} onLogin={handleLoginRedirect} />
        <UserProvider>
          <Routes>
            {/* Rota de callback para o OIDC. Exibe um loader enquanto o AuthProvider processa. */}
            <Route path="/school" element={<AuthCallback />} />

            {/* 🔒 Rotas protegidas */}
            {/* <Route path="/pedido-operacao" element={<ProtectedPage> <Index /> </ProtectedPage>} /> */}
            {/* <Route path="/" index element={<ProtectedPage><HolidaySuspension /></ProtectedPage>} /> */}
            {/* <Route path="/" index element={<ProtectedPage><Index /></ProtectedPage>} /> */}
            <Route index element={<Navigate to="/customer-portal" replace />} />

            <Route element={<AdminRouteGuard />}>
              <Route path="/admin-pedidos" element={<ProtectedPage><AdminOrders /></ProtectedPage>} />
              <Route path="/historico-pedidos" element={<ProtectedPage><OrderHistory /></ProtectedPage>} />
              <Route path="/catalogo-produtos" element={<ProtectedPage><ProductCatalog /></ProtectedPage>} />
              <Route path="/cadastro-produto" element={<ProtectedPage><ProductRegistration /></ProtectedPage>} />
              <Route path="/cadastro-produto/:id" element={<ProtectedPage> <ProductRegistration /> </ProtectedPage>} />
              <Route path="/cadastro-cliente" element={<ProtectedPage> <ClientProfileRegistration /> </ProtectedPage>} />
              <Route path="/faturamentos" element={<ProtectedPage><InvoicingList /></ProtectedPage>} />
              <Route path="/ciclos-comerciais" element={<ProtectedPage><CyclesList /></ProtectedPage>} />
              <Route path="/configuracoes-pagamento" element={<ProtectedPage><PaymentConfigurations /></ProtectedPage>} />
              {isCustomerGroupAddressEnabled && (
                <Route path="/importar-grupos-cliente" element={<ProtectedPage><CustomerGroupImport /></ProtectedPage>} />
              )}
              {isNewScheduledOrderCycleEnabled && (
                <>
                  <Route path="/politicas-corte" element={<ProtectedPage><ScheduledOrderCutoffPolicies /></ProtectedPage>} />
                  <Route path="/produtos-programados" element={<ProtectedPage><ScheduledOrderRules /></ProtectedPage>} />
                  {isExamShipmentDistributionEnabled && (
                    <Route path="/distribuicao-remessas" element={<ProtectedPage><ExamShipmentDistribution /></ProtectedPage>} />
                  )}
                </>
              )}
              {isIntegratedOrdersEnabled && (
                <Route path="/pedidos-integrados" element={<ProtectedPage><IntegratedOrders /></ProtectedPage>} />
              )}
              {isInvoiceReturnsEnabled && (
                <Route path="/retornos-erp" element={<ProtectedPage><InvoiceReturns /></ProtectedPage>} />
              )}
              {canOverrideDynamicsEnvironment && (
                <>
                  <Route path="/configuracoes-sessao" element={<ProtectedPage><SessionSettings /></ProtectedPage>} />
                  <Route path="/diagnostico-apis" element={<ProtectedPage><ApiDiagnostics /></ProtectedPage>} />
                  {isNewOrderZendeskTicketEnabled && (
                    <Route path="/chamados-zendesk" element={<ProtectedPage><ZendeskTicketSettings /></ProtectedPage>} />
                  )}
                </>
              )}

              {/* <Route path="/ordens-simuladas" element={<ProtectedPage><SimulatedOrders /></ProtectedPage>} /> */}
            </Route>

            {/* Customer Portal Routes - With Layout */}
            <Route path="/customer-portal" element={<CustomerPortalLayout />}>
              <Route index element={<ProtectedPage><Dashboard /></ProtectedPage>} />
              <Route path="pedidos" element={<ProtectedPage><MyOrders /></ProtectedPage>} />
              <Route path="pedido-programado" element={<ProtectedPage><MyScheduledOrder /></ProtectedPage>} />
              {/* Em manutenção: rota trocada temporariamente de <OrderInterface /> para <OrderMaintenance />. */}
              {/* <Route path="realizar-pedido" element={<ProtectedPage><OrderMaintenance /></ProtectedPage>} /> */}
              <Route path="realizar-pedido" element={<ProtectedPage><OrderInterface /></ProtectedPage>} />
              <Route path="realizar-pedido/2027-visualizacao" element={<ProtectedPage><Simulation2027 /></ProtectedPage>} />
              <Route path="financeiro" element={<ProtectedPage><Financial /></ProtectedPage>} />
              {/* Página de teste fora do menu, acessada só por link (validação em produção). */}
              <Route path="teste-visao-financeira" element={<ProtectedPage><CustomerPortalOverviewTest /></ProtectedPage>} />
              <Route path="tabela-precos" element={<ProtectedPage><ProductPricing /></ProtectedPage>} />
              <Route path="pedidos/:orderId" element={<ProtectedPage><OrderDetails /></ProtectedPage>} />
            </Route>
            {/* Backoffice Routes - With Layout */}
            <Route element={<AdminRouteGuard />}>
              <Route path="/backoffice" element={<CustomerPortalLayout />}>
                <Route index element={<Navigate to="/backoffice/pedidos" replace />} />
                <Route path="pedidos" element={<ProtectedPage><OrderManagement /></ProtectedPage>} />
                <Route path="pedido-programados" element={<ProtectedPage><ScheduledOrder /></ProtectedPage>} />
                <Route path="simulados" element={<ProtectedPage><SimulatedOrdersDashboard /></ProtectedPage>} />
                <Route path="perfil-clientes" element={<ProtectedPage><ClientsProfile /></ProtectedPage>} />
                <Route path="perfil-clientes/:clientId/gerenciar-produtos" element={<ProtectedPage><ManageProducts /></ProtectedPage>} />
                <Route path="clients" element={<ProtectedPage><ClientsList /></ProtectedPage>} />
                <Route path="client/:clientCode" element={<ProtectedPage><ClientDetails /></ProtectedPage>} />
                <Route path="pedidos/:orderId" element={<ProtectedPage><OrderDetails /></ProtectedPage>} />
                <Route path="reajuste" element={<ProtectedPage><ClientAdjustment /></ProtectedPage>} />
                {isCouponEnabled && (
                  <Route path="gestao-comercial/cupons" element={<ProtectedPage><CouponManagement /></ProtectedPage>} />
                )}
                <Route path="gestao-comercial/faturamento-clientes" element={<ProtectedPage><CommercialRevenueDashboard /></ProtectedPage>} />
                <Route path="gestao-comercial/faturamento-clientes/:clientId" element={<ProtectedPage><CommercialRevenueClientDetails /></ProtectedPage>} />
                {isOrderItemsReportEnabled && (
                  <Route path="relatorios/itens-pedido" element={<ProtectedPage><OrderItemsReport /></ProtectedPage>} />
                )}

                {isCustomerGroupAddressEnabled && (
                  <Route path="clientes/grupos" element={<ProtectedPage><CustomerGroupsList /></ProtectedPage>} />
                )}
                {isPhysicalClientEnabled && (
                  <>
                    <Route path="clientes/pf" element={<ProtectedPage><PhysicalClientsList /></ProtectedPage>} />
                    <Route path="clientes/pf/novo" element={<ProtectedPage><PhysicalClientCreate /></ProtectedPage>} />
                    <Route path="clientes/pf/:id" element={<ProtectedPage><PhysicalClientDetails /></ProtectedPage>} />
                  </>
                )}
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </UserProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
